import moment from 'moment'

import { PlaceResult } from 'tripPlanner/types/google'
import getPlaceCacheDb from 'tripPlanner/utils/placesIndexDB'

type PlacePhoto = {
  width: number
  height: number
  url: string
}

type PhotoOrientation = 'portrait' | 'landscape' | 'all'

const getOrientedPhotoUrl = (
  photos: PlacePhoto[],
  orientation: PhotoOrientation,
): string | null => {
  if (orientation === 'all') {
    return photos[0].url
  }
  const orientedPhotos = photos.filter((photo) => {
    const isLandscape = photo.width > photo.height
    return orientation === 'landscape' ? isLandscape : !isLandscape
  })
  if (orientedPhotos?.[0]) {
    const photo = orientedPhotos[0]

    return photo.url
  }
  return null
}

const getCachedPlaceImageUrl = async (
  placeId: string,
  orientation: PhotoOrientation,
  maxWidth?: number,
  maxHeight?: number,
) => {
  const db = getPlaceCacheDb()
  if (!db) return null

  const place = await db.get(placeKey(placeId, maxWidth, maxHeight))
  const expiresOn = moment(place?.fetchedOn).add(1, 'day')
  if (place && moment().isBefore(expiresOn)) {
    return getOrientedPhotoUrl(place.photos, orientation)
  }
  return null
}

const placeKey = (placeId: string, maxWidth?: number, maxHeight?: number) =>
  `${placeId}-${maxWidth ?? ''}-${maxHeight ?? ''}`

const getPlaceDetailsForPhotos = async (
  placeService: google.maps.places.PlacesService,
  placeId: string,
): Promise<PlaceResult | null> => {
  return new Promise((resolve, reject) => {
    placeService.getDetails(
      { placeId, fields: ['photos', 'place_id', 'vicinity'] },
      (
        placeRes: PlaceResult | null,
        status: google.maps.places.PlacesServiceStatus | null,
      ) => {
        if (status === google.maps.places.PlacesServiceStatus.OK) {
          resolve(placeRes)
        } else {
          reject(status)
        }
      },
    )
  })
}

const findPlacesForPhots = async (
  placeService: google.maps.places.PlacesService,
  query: string,
): Promise<PlaceResult[] | null> => {
  return new Promise((resolve, reject) => {
    placeService.findPlaceFromQuery(
      { query, fields: ['photos', 'place_id'] },
      (
        results: PlaceResult[] | null,
        status: google.maps.places.PlacesServiceStatus | null,
      ) => {
        if (status === google.maps.places.PlacesServiceStatus.OK) {
          resolve(results)
        } else {
          reject(status)
        }
      },
    )
  })
}

const getImageUrlFromPlace = (
  place: PlaceResult,
  placeId: string,
  orientation: PhotoOrientation,
  maxWidth?: number,
  maxHeight?: number,
): string | null => {
  if (place.photos) {
    const photos = place.photos.map((photo) => ({
      width: photo.width,
      height: photo.height,
      url: photo.getUrl({ maxWidth, maxHeight }),
    }))
    const db = getPlaceCacheDb()
    if (db) {
      db.set(placeKey(placeId, maxWidth, maxHeight), {
        photos,
        fetchedOn: new Date(),
      })
    }
    return getOrientedPhotoUrl(photos, orientation)
  }
  return null
}

export const getPlaceImageUrl = async (
  placeService: google.maps.places.PlacesService,
  placeId: string,
  orientation: PhotoOrientation,
  maxWidth?: number,
  maxHeight?: number,
) => {
  const cachedPlaceImageUrl = await getCachedPlaceImageUrl(
    placeId,
    orientation,
    maxWidth,
    maxHeight,
  )

  if (cachedPlaceImageUrl) return cachedPlaceImageUrl

  if (placeService) {
    const place = await getPlaceDetailsForPhotos(placeService, placeId)
    if (place) {
      const imageUrl = getImageUrlFromPlace(
        place,
        placeId,
        orientation,
        maxWidth,
        maxHeight,
      )
      if (imageUrl) {
        return imageUrl
      }
      if (place.vicinity) {
        const places = await findPlacesForPhots(placeService, place.vicinity)
        if (places && places.length > 0) {
          return getImageUrlFromPlace(
            places[0],
            placeId,
            orientation,
            maxWidth,
            maxHeight,
          )
        }
      }
    }
  }
  return null
}
